import { useState, useEffect, useMemo } from 'react';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from 'recharts';
import { fetchData } from '../hooks/useDataRefresh';
import { WW_FY26_PROMOS, COLES_FY26_PROMOS } from '../data/promo-calendar';
import ScanDataTable from '../components/ScanDataTable';

// FY2026: Week 1 = Wed 2 Jul 2025
const FY_START = new Date('2025-07-02');
const FY2025_START = new Date('2024-07-03'); // 52 weeks before FY2026
const BIAS_WINDOW = 8; // weeks for bias tolerance check
const BIAS_THRESHOLD = 15; // ±15%

// Fallback: derive last actual week from JSON data if metadata not present
function deriveLastActualWeek(skuData) {
  let max = 0;
  for (const s of skuData) {
    for (const key of ['ww', 'coles', 'costco', 'aldi']) {
      for (const w of (s[key] || [])) {
        if ((w.ty_rsv > 0 || w.ty_units > 0) && w.week > max) max = w.week;
      }
    }
  }
  return max || null;
}

function weekEndingLabel(week) {
  // Week N ends on Tuesday (6 days after the Wed start of that week)
  const d = new Date(FY_START);
  d.setDate(d.getDate() + (week - 1) * 7 + 6);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

// AU format: WC D/M/YY  e.g. "11/3/26"
function weekToLabel(w, fy2025 = false) {
  const d = fy2025 ? new Date(FY2025_START) : new Date(FY_START);
  d.setDate(d.getDate() + (w - 1) * 7);
  const day = d.getDate();
  const mon = d.getMonth() + 1;
  const yr  = String(d.getFullYear()).slice(-2);
  return `${day}/${mon}/${yr}`;
}

function itemBrand(item) {
  if (item.brand === 'INFUZIONS') {
    return item.assortment?.includes('MULTIPACK') ? 'Infuzions Multipack' : 'Infuzions';
  }
  if (item.brand === 'BHUJA') return 'Bhuja';
  if (item.brand === 'ALDI' || item.brand === 'OH SO NATURAL') return 'Private Label';
  return 'Other';
}

function itemAssortmentGroup(item) {
  const a = item.assortment || '';
  // Private label brands (ALDI-branded, Oh So Natural) always group as Private Label
  if (item.brand === 'ALDI' || item.brand === 'OH SO NATURAL') return 'Private Label';
  // Costco exclusive — split by brand (Bhuja vs Infuzions large pack)
  if (a === 'COSTCO EXCLUSIVE') return item.brand === 'BHUJA' ? 'Bhuja' : 'Infuzions';
  if (item.brand === 'BHUJA') {
    if (a.includes('300-400') || a.includes('1KG')) return 'Bhuja 300g+';
    if (a.includes('AUTHENTIC') || a.includes('DELI')) return 'Bhuja Authentic';
    if (a.includes('MULTIPACK')) return 'Bhuja Multipack 125g';
    if (a.includes('40-50')) return 'Bhuja 40-50g';
    return 'Bhuja 70-200g';
  }
  if (item.brand === 'INFUZIONS') {
    if (a.includes('AIR FRIES')) return 'Infuzions Air Fries 90g';
    if (item.product?.includes('AIR FRIES')) return 'Infuzions Air Fries Multipack 75g';
    if (a.includes('MULTIPACK')) return 'Infuzions Multipack 75g';
    return 'Infuzions 90-110g';
  }
  return 'Other';
}

// Map customer name → JSON array key
function customerKey(customer) {
  return customer === 'WW' ? 'ww' : customer === 'Coles' ? 'coles' : customer === 'Metcash' ? 'metcash' : customer === 'ALDI' ? 'aldi' : 'costco';
}

// For non-scan customers only show brands that have actual forecast data
function hasCustomerData(sku, cKey) {
  return (sku[cKey] || []).length > 0;
}

// Maps UI assortment group names → promo calendar assortment labels
// SKU assortment codes (e.g. "BHUJA 70-200G") differ from promo calendar names
const PROMO_GROUP_MAP = {
  'Bhuja 70-200g':           ['Bhuja 140-160g & Desi'],
  'Bhuja Authentic':         ['Bhuja 140-160g & Desi'],
  'Bhuja 40-50g':            ['Bhuja 50g (EDV)'],
  'Bhuja 300g+':             ['Bhuja 350g'],
  'Bhuja':                   [],
  'Infuzions 90-110g':       ['Infuzions 90g'],
  'Infuzions Air Fries 90g': ['Infuzions Air Fries 90g', 'Infuzions AF Multipacks'],
  'Infuzions Multipack 75g': ['Infuzions Multi 5x15g'],
  'Infuzions':               [],
  'Bhuja Multipack 125g':    [],
  'Private Label':           [],
};

// Convert UI assortment groups in filteredSkus → promo calendar assortment names
function toPromoAssortments(skuList) {
  const groups = new Set(skuList.map(itemAssortmentGroup));
  const names = new Set();
  for (const g of groups) {
    for (const n of (PROMO_GROUP_MAP[g] || [])) names.add(n);
  }
  return names.size > 0 ? names : null; // null = show all
}

// Promo calendar lookup — filter to only selected SKUs' assortments
function getPromoForWeek(week, retailer, promoAssortments) {
  if (!['WW', 'Coles'].includes(retailer)) return [];
  const promos = retailer === 'WW' ? WW_FY26_PROMOS : COLES_FY26_PROMOS;
  const w = String(week);
  return promos
    .filter(p => !promoAssortments || promoAssortments.has(p.assortment))
    .map(p => ({
      assortment: p.assortment,
      price: p.data?.['PROMO PRICE']?.[w],
      tactic: p.data?.['PROMO/MEDIA']?.[w] || p.data?.['TACTIC']?.[w],
    }))
    .filter(p => p.price || p.tactic);
}

// Build a multi-line tooltip string for upcoming promos for a given set of SKUs
function buildPromoTooltip(skuList, retailer, upcomingWeeks) {
  if (!['WW', 'Coles'].includes(retailer) || !upcomingWeeks?.length) return null;
  const promoAssortments = toPromoAssortments(skuList);
  const lines = [];
  for (const wk of upcomingWeeks) {
    for (const pr of getPromoForWeek(wk, retailer, promoAssortments)) {
      const parts = [`Wk${wk} · ${pr.assortment}`];
      if (pr.price) parts.push(pr.price);
      if (pr.tactic) parts.push(pr.tactic);
      lines.push(parts.join(' · '));
    }
  }
  return lines.length ? lines.join('\n') : null;
}

// Period options: { label, historyWeeks, futureWeeks } — history = actuals to show, future = forecast weeks
const PERIOD_OPTIONS = [
  { label: '-52W+13W', historyWeeks: 52, futureWeeks: 13 },
  { label: '-26W+13W', historyWeeks: 26, futureWeeks: 13 },
  { label: '4W',       historyWeeks: 4,  futureWeeks: 0 },
  { label: '13W',      historyWeeks: 13, futureWeeks: 0 },
  { label: '26W',      historyWeeks: 26, futureWeeks: 0 },
  { label: '52W',      historyWeeks: 52, futureWeeks: 0 },
  { label: '104W',     historyWeeks: 104, futureWeeks: 0 },
];

function fmtVal(v, metric) {
  if (v == null) return null;
  return metric === 'Units'
    ? `${(v / 1000).toFixed(1)}K u`
    : `$${(v / 1000).toFixed(0)}K`;
}

function CustomPromoTooltip({ active, payload, label, retailer, metric, filteredAssortments }) {
  if (!active || !payload?.length) return null;
  const week = payload[0]?.payload?.week;
  const promos = week ? getPromoForWeek(week, retailer, filteredAssortments) : [];

  return (
    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 8, padding: '10px 14px', fontSize: 12, maxWidth: 280 }}>
      <div style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: 6 }}>WC {label} 26</div>
      {payload.filter(p => p.value != null).map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, background: p.color, flexShrink: 0 }} />
          <span style={{ color: 'var(--text-body)' }}>{p.name}:</span>
          <span style={{ color: 'var(--text-primary)' }}>
            {p.name === 'Avg Price' ? `$${p.value?.toFixed(2)}` : fmtVal(p.value, metric)}
          </span>
        </div>
      ))}
      {promos.length > 0 && (
        <div style={{ marginTop: 8, borderTop: '1px solid var(--border-soft)', paddingTop: 6 }}>
          {promos.map((pr, i) => (
            <div key={i} style={{ marginBottom: 3 }}>
              <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>{pr.assortment}: </span>
              {pr.price && <span style={{ color: 'var(--warn)', fontSize: 11, fontWeight: 600 }}>{pr.price}</span>}
              {pr.tactic && <span style={{ color: 'var(--text-muted)', fontSize: 10 }}> · {pr.tactic}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const LINE_COLORS = {
  actual:    null,  // uses chart color prop
  ly:        'var(--text-muted)',
  custFcst:  '#06b6d4',
  mfgFcst:   '#f59e0b',
};

function ForecastChart({ title, color, weeks, metric, retailer, showPrice, filteredAssortments, lastActualWeek, visibility }) {
  const fmtY = metric === 'Units'
    ? v => `${(v / 1000).toFixed(0)}K`
    : v => `$${(v / 1000).toFixed(0)}K`;

  const vis = visibility || { actual: true, ly: true, custFcst: true, mfgFcst: true, avgPrice: true };

  return (
    <div className="card">
      <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>{title}</div>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={weeks} margin={{ left: 8, right: showPrice ? 48 : 16, top: 4, bottom: 28 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 8, angle: -45, textAnchor: 'end' }} axisLine={false} tickLine={false} interval={0} height={40} />
          <YAxis yAxisId="left" tickFormatter={fmtY} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} width={52} />
          {showPrice && (
            <YAxis yAxisId="right" orientation="right" tickFormatter={v => `$${v.toFixed(2)}`} tick={{ fill: 'var(--text-muted)', fontSize: 9 }} axisLine={false} tickLine={false} width={40} domain={['auto', 'auto']} />
          )}
          <Tooltip content={<CustomPromoTooltip retailer={retailer} metric={metric} filteredAssortments={filteredAssortments} />} />
          <Legend wrapperStyle={{ fontSize: 11, color: 'var(--text-body)' }} />

          {vis.ly        && <Line yAxisId="left" dataKey="ly"        name="LY"            stroke={LINE_COLORS.ly}        strokeWidth={1.5} strokeDasharray="4 3" dot={false} connectNulls />}
          {vis.actual    && <Line yAxisId="left" dataKey="actual"    name="Actual"         stroke={color}                 strokeWidth={3}   dot={false} connectNulls />}
          {vis.custFcst  && <Line yAxisId="left" dataKey="custFcst"  name="Cust Forecast"  stroke={LINE_COLORS.custFcst}  strokeWidth={2.5} strokeDasharray="8 3" dot={false} connectNulls />}
          {vis.mfgFcst   && <Line yAxisId="left" dataKey="mfgFcst"   name="Mfg Forecast"   stroke={LINE_COLORS.mfgFcst}   strokeWidth={2.5} strokeDasharray="6 3" dot={{ r: 2, fill: LINE_COLORS.mfgFcst }} />}
          {showPrice && (
            <Line yAxisId="right" dataKey="avgPrice" name="Avg Price" stroke="#a855f7" strokeWidth={1} strokeDasharray="2 2" dot={false} connectNulls />
          )}

          {lastActualWeek && (
            <ReferenceLine
              yAxisId="left"
              x={weekToLabel(lastActualWeek + 1)}
              stroke="#3b82f6"
              strokeDasharray="4 2"
              label={{ value: '← Actual  |  Forecast →', fill: '#3b82f6', fontSize: 10, position: 'top' }}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function BiasDashboard({ weeks, metric, confirmedLastWeek }) {
  // Use only confirmed complete weeks (exclude current partial week)
  const window8 = weeks.filter(w => w.actual != null && (confirmedLastWeek == null || w.week <= confirmedLastWeek)).slice(-BIAS_WINDOW);

  // 8-week totals — M-Forecast vs Actual
  const totActual = window8.reduce((s, w) => s + (w.actual || 0), 0);
  const totMfg    = window8.filter(w => w.mfgFcst != null).reduce((s, w) => s + (w.mfgFcst || 0), 0);
  const hasMfg    = window8.some(w => w.mfgFcst != null);

  // Bias = same M-Forecast vs Actual comparison, so gauge matches the totals above
  const bias = hasMfg && totActual > 0 ? ((totMfg - totActual) / totActual) * 100 : null;

  const fmtV = v => v == null ? '—' : metric === 'Units'
    ? (v >= 1000 ? `${(v / 1000).toFixed(1)}K u` : `${v.toLocaleString()} u`)
    : `$${(v / 1000).toFixed(0)}K`;

  // Week-ending date for the last confirmed week
  const weekEndDate = (() => {
    if (!confirmedLastWeek) return null;
    const d = new Date(FY_START);
    d.setDate(d.getDate() + (confirmedLastWeek - 1) * 7 + 6);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'numeric', year: '2-digit' });
  })();

  const color = bias == null ? 'var(--text-muted)' : Math.abs(bias) <= BIAS_THRESHOLD ? 'var(--success)' : 'var(--danger)';
  const pct   = bias != null ? Math.min(Math.abs(bias) / BIAS_THRESHOLD, 2) * 50 : 0;
  const isOver = bias != null && Math.abs(bias) > BIAS_THRESHOLD;

  return (
    <div className="card">
      <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Forecast Bias Tolerance</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>
        Mfg forecast vs actuals — {BIAS_WINDOW} weeks ending {weekEndDate || '—'} · ±{BIAS_THRESHOLD}% tolerance · {metric === 'Units' ? 'units' : 'RSV'} · adjusts to filter selection
      </div>

      {/* 8-week totals */}
      {window8.length > 0 && (
        <div style={{ display: 'flex', gap: 24, marginBottom: 14, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Actual {window8.length}W</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{fmtV(totActual)}</div>
          </div>
          {hasMfg && (
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>M-Forecast {window8.length}W</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--warn)' }}>{fmtV(totMfg)}</div>
            </div>
          )}
          {hasMfg && totActual > 0 && (
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Variance</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: totMfg > totActual ? 'var(--warn)' : 'var(--info)' }}>
                {`${totMfg >= totActual ? '+' : ''}${(((totMfg - totActual) / totActual) * 100).toFixed(1)}%`}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Gauge */}
      <div style={{ background: 'var(--bg-elevated)', borderRadius: 8, padding: '12px 16px', border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ color: 'var(--text-body)', fontSize: 12, fontWeight: 600 }}>Bias</span>
          <span style={{ fontSize: 20, fontWeight: 700, color }}>
            {bias == null ? '—' : `${bias >= 0 ? '+' : ''}${bias.toFixed(1)}%`}
          </span>
        </div>
        <div style={{ position: 'relative', height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: isOver ? 'var(--danger)' : 'var(--success)', borderRadius: 3, transition: 'width 0.3s ease' }} />
          <div style={{ position: 'absolute', left: '50%', top: 0, width: 2, height: '100%', background: 'var(--warn)', transform: 'translateX(-1px)' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: 'var(--text-muted)' }}>
          <span>0%</span>
          <span style={{ color: 'var(--warn)' }}>±{BIAS_THRESHOLD}% threshold</span>
          <span>{BIAS_THRESHOLD * 2}%+</span>
        </div>
        {isOver && (
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--danger)' }}>
            ⚠ Mfg forecast {bias > 0 ? 'over' : 'under'}-calling by {Math.abs(bias).toFixed(1)}% vs actuals (last {BIAS_WINDOW} weeks)
          </div>
        )}
      </div>
    </div>
  );
}

export default function Forecast() {
  const [skuData, setSkuData]           = useState([]);
  const [lastActualWeek, setLastActualWeek] = useState(null);
  const [metric, setMetric]             = useState('Units');
  const [customer, setCustomer]         = useState('WW');       // WW | Coles
  const [brand, setBrand]               = useState('All');
  const [assortmentGroup, setAssortmentGroup] = useState('All');
  const [sku, setSku]                   = useState('All');
  const [skuMulti, setSkuMulti]         = useState(new Set());
  const [hiddenGraphs, setHiddenGraphs] = useState(new Set());
  const [period, setPeriod]             = useState('-26W+13W');
  const [breakdownMode, setBreakdownMode] = useState('assortment'); // 'assortment' | 'sku'
  const [visibility, setVisibility] = useState({ actual: true, ly: true, custFcst: true, mfgFcst: true, avgPrice: true });

  useEffect(() => {
    fetchData('scanv2-sku-weekly.json').then(data => {
      // Support both old array format and new {metadata, skus} format
      const skus = Array.isArray(data) ? data : (data.skus || []);
      const metaWeek = Array.isArray(data) ? null : data.metadata?.last_actual_week;
      setSkuData(skus);
      setLastActualWeek(metaWeek || deriveLastActualWeek(skus));
    });
  }, []);

  // For non-scan customers (Metcash, ALDI, Costco) only show brands/SKUs with data
  const relevantSkus = useMemo(() => {
    const cKey = customerKey(customer);
    const scanCustomers = ['WW', 'Coles'];
    return scanCustomers.includes(customer)
      ? skuData  // scan customers: all brands eligible
      : skuData.filter(s => hasCustomerData(s, cKey));
  }, [skuData, customer]);

  const brandOptions = useMemo(() => {
    const seen = new Set(relevantSkus.map(itemBrand));
    const order = ['Bhuja', 'Infuzions', 'Infuzions Multipack', 'Other'];
    return ['All', ...order.filter(b => seen.has(b))];
  }, [relevantSkus]);

  const assortmentOptions = useMemo(() => {
    const items = brand === 'All' ? relevantSkus : relevantSkus.filter(s => itemBrand(s) === brand);
    return ['All', ...new Set(items.map(itemAssortmentGroup)).values()].filter(a => a !== 'Other');
  }, [relevantSkus, brand]);

  const skuOptions = useMemo(() => {
    let items = relevantSkus;
    if (brand !== 'All') items = items.filter(s => itemBrand(s) === brand);
    if (assortmentGroup !== 'All') items = items.filter(s => itemAssortmentGroup(s) === assortmentGroup);
    return ['All', ...items.map(s => s.product)];
  }, [relevantSkus, brand, assortmentGroup]);

  const filteredSkus = useMemo(() => {
    let items = relevantSkus;
    if (brand !== 'All') items = items.filter(s => itemBrand(s) === brand);
    if (assortmentGroup !== 'All') items = items.filter(s => itemAssortmentGroup(s) === assortmentGroup);
    if (skuMulti.size > 0) items = items.filter(s => skuMulti.has(s.product));
    else if (sku !== 'All') items = items.filter(s => s.product === sku);
    return items;
  }, [relevantSkus, brand, assortmentGroup, sku, skuMulti]);

  // Promo calendar assortment names for the current selection — used to filter promo tooltip
  const filteredAssortments = useMemo(() => toPromoAssortments(filteredSkus), [filteredSkus]);

  // Period config — depends on lastActualWeek derived from SCANV2 data
  const periodConfig = PERIOD_OPTIONS.find(p => p.label === period) || PERIOD_OPTIONS[1];
  const displayWeeks = useMemo(() => {
    if (!lastActualWeek) return [];
    const histStart = lastActualWeek - periodConfig.historyWeeks + 1;
    const end = lastActualWeek + periodConfig.futureWeeks;
    return Array.from({ length: end - histStart + 1 }, (_, i) => histStart + i);
  }, [periodConfig, lastActualWeek]);

  // Upcoming weeks (after lastActualWeek) within display window — for pill tooltips
  const upcomingWeeks = useMemo(
    () => displayWeeks.filter(w => w > (lastActualWeek || 0)),
    [displayWeeks, lastActualWeek]
  );

  function buildWeeks(retailerKey, skuList) {
    if (!lastActualWeek) return [];
    const key = customerKey(retailerKey);
    // Determine if this retailer has real scan actuals (ty_rsv / ty_units).
    // Detected dynamically — Metcash, ALDI, and Costco all detected the same way.
    const isNonScan =
      !skuList.some(s => (s[key] || []).some(w => (w.ty_rsv || 0) > 0 || (w.ty_units || 0) > 0));

    // Build TY, LY, customer forecast, and mfg forecast lookups by week number
    const tyByWeek   = {};
    const lyByWeek   = {};
    const cfByWeek   = {}; // customer forecast units (from WWFORECAST / COLESFORECAST)
    const mfgByWeek  = {}; // M-Forecast consumer units from FORECAST.xlsx

    for (const s of skuList) {
      for (const w of (s[key] || [])) {
        // M-Forecast from FORECAST.xlsx (all weeks)
        if (w.mfg_fcst_units > 0) {
          mfgByWeek[w.week] = (mfgByWeek[w.week] || 0) + w.mfg_fcst_units;
        }
        // TY actuals — only weeks with real scan data (WW/Coles only)
        if (w.ty_rsv > 0 || w.ty_units > 0) {
          if (!tyByWeek[w.week]) tyByWeek[w.week] = { rsv: 0, units: 0, psum: 0, punits: 0 };
          tyByWeek[w.week].rsv   += w.ty_rsv  || 0;
          tyByWeek[w.week].units += w.ty_units || 0;
          if (w.avgPrice && w.ty_units) {
            tyByWeek[w.week].psum   += w.avgPrice * w.ty_units;
            tyByWeek[w.week].punits += w.ty_units;
          }
        }
        // LY
        if (w.ly_rsv > 0 || w.ly_units > 0) {
          if (!lyByWeek[w.week]) lyByWeek[w.week] = { rsv: 0, units: 0 };
          lyByWeek[w.week].rsv   += w.ly_rsv   || 0;
          lyByWeek[w.week].units += w.ly_units  || 0;
        }
        // Customer forecast units from SCANV2 (WWFORECAST / COLESFORECAST)
        if (w.cust_fcst_units > 0) {
          cfByWeek[w.week] = (cfByWeek[w.week] || 0) + w.cust_fcst_units;
        }
      }
    }

    // Last known avg price — for converting forecast units → RSV estimate (scan customers only)
    // Falls back to WW price if no price found for this retailer (e.g. before first Coles actual loads)
    const lastKnownAvgPrice = (() => {
      for (let wk = lastActualWeek; wk >= 1; wk--) {
        const d = tyByWeek[wk];
        if (d?.punits > 0) return d.psum / d.punits;
      }
      // Fallback: use WW price for same SKUs (RSPs are identical or near-identical)
      if (['coles', 'metcash', 'aldi', 'costco'].includes(key)) {
        for (let wk = lastActualWeek; wk >= 1; wk--) {
          for (const s of skuList) {
            const ww = (s.ww || []).find(w => w.week === wk);
            if (ww?.avgPrice > 0 && ww?.ty_units > 0) return ww.avgPrice;
          }
        }
      }
      return null;
    })();

    return displayWeeks.map(v => {
      const isScanActual = v > 0 && v <= lastActualWeek && !!tyByWeek[v];
      const isPastWeek   = v > 0 && v <= lastActualWeek;
      const isFcst       = v > lastActualWeek;
      const isLYHist     = v <= 0;

      // Helper: convert mfg units to display value (units or estimated RSV)
      const mfgUnitsToValue = (units) =>
        units == null ? null
          : metric === 'Units' ? units
          : lastKnownAvgPrice ? Math.round(units * lastKnownAvgPrice) : null;

      const mfgFcstUnits = mfgByWeek[v] || null;

      // Actual and M-Forecast split:
      // - Scan customers (WW/Coles): actual = scan data; mfgFcst = M-Forecast all weeks
      // - Non-scan customers (ALDI/Costco/Metcash): actual = mfg sell-in for past weeks;
      //   mfgFcst = mfg forecast for future weeks only (past weeks show on actual line)
      let actual = null;
      let mfgFcst = null;

      if (isNonScan) {
        // For past weeks, treat mfg data as actuals (it represents actual sell-in).
        // For future weeks, show on the M-Forecast line.
        if (isPastWeek || isLYHist) {
          actual = mfgUnitsToValue(mfgFcstUnits);
        } else {
          mfgFcst = mfgUnitsToValue(mfgFcstUnits);
        }
      } else {
        // Scan customers: actuals from SCANV2
        if (isScanActual) {
          const d = tyByWeek[v];
          actual = d ? (metric === 'Units' ? d.units : d.rsv) || null : null;
        } else if (isLYHist) {
          const lyWk = 52 + v; // v=0→week52 LY, v=-1→week51, etc.
          if (lyWk >= 1) {
            const d = lyByWeek[lyWk];
            actual = d ? (metric === 'Units' ? d.units : d.rsv) || null : null;
          }
        }
        // M-Forecast shows on all weeks for scan customers
        mfgFcst = mfgUnitsToValue(mfgFcstUnits);
      }

      // LY reference — real SCANV2 LY records (scan customers only)
      let ly = null;
      if (!isNonScan && v > 0) {
        const d = lyByWeek[v];
        ly = d ? (metric === 'Units' ? d.units : d.rsv) || null : null;
      }

      // Customer forecast — from WWFORECAST / COLESFORECAST via build script
      let custFcst = null;
      if (v > 0 && cfByWeek[v]) {
        const fcstUnits = cfByWeek[v];
        custFcst = metric === 'Units'
          ? fcstUnits
          : lastKnownAvgPrice ? Math.round(fcstUnits * lastKnownAvgPrice) : null;
      }

      // Avg price (single SKU, scan actuals only)
      let avgPrice = null;
      if (isScanActual) {
        const d = tyByWeek[v];
        avgPrice = d?.punits > 0 ? d.psum / d.punits : null;
      }

      // AU date label: D/M/YY
      const date = v > 0 ? weekToLabel(v) : weekToLabel(52 + v, true);

      return { week: v, date, actual, ly, custFcst, mfgFcst, avgPrice };
    });
  }

  // Main chart data
  const wwWeeks      = useMemo(() => filteredSkus.length && lastActualWeek ? buildWeeks('WW', filteredSkus) : [], [filteredSkus, metric, displayWeeks, lastActualWeek]);
  const colesWeeks   = useMemo(() => filteredSkus.length && lastActualWeek ? buildWeeks('Coles', filteredSkus) : [], [filteredSkus, metric, displayWeeks, lastActualWeek]);
  const metcashWeeks = useMemo(() => filteredSkus.length && lastActualWeek ? buildWeeks('Metcash', filteredSkus) : [], [filteredSkus, metric, displayWeeks, lastActualWeek]);
  const aldiWeeks    = useMemo(() => filteredSkus.length && lastActualWeek ? buildWeeks('ALDI', filteredSkus) : [], [filteredSkus, metric, displayWeeks, lastActualWeek]);
  const costcoWeeks  = useMemo(() => filteredSkus.length && lastActualWeek ? buildWeeks('Costco', filteredSkus) : [], [filteredSkus, metric, displayWeeks, lastActualWeek]);

  // Per-SKU breakdown charts (when breakdownMode === 'sku' and a group is selected)
  const skuBreakdownItems = useMemo(() => {
    if (breakdownMode !== 'sku' || !lastActualWeek) return [];
    return filteredSkus.map(s => ({
      label: s.product.replace('INFUZIONS ', '').replace('BHUJA ', ''),
      weeks: buildWeeks(customer, [s]),
      filteredAssortments: toPromoAssortments([s]),
    }));
  }, [filteredSkus, metric, displayWeeks, breakdownMode, customer, lastActualWeek]);

  const showPrice = visibility.avgPrice;

  const activeRetailerKey = customer;
  const activeWeeks = customer === 'WW' ? wwWeeks : customer === 'Coles' ? colesWeeks : customer === 'Metcash' ? metcashWeeks : customer === 'ALDI' ? aldiWeeks : costcoWeeks;
  const activeColor = customer === 'WW' ? '#1a8a3f' : customer === 'Coles' ? '#e03131' : customer === 'Metcash' ? '#f97316' : customer === 'ALDI' ? '#a16207' : '#0891b2';
  const activeName  = customer === 'WW' ? 'Woolworths' : customer === 'Coles' ? 'Coles' : customer === 'Metcash' ? 'Metcash' : customer === 'ALDI' ? 'ALDI' : 'Costco';

  const handleBrandChange      = (b) => { setBrand(b); setAssortmentGroup('All'); setSku('All'); setSkuMulti(new Set()); setHiddenGraphs(new Set()); };
  const handleAssortmentChange = (a) => { setAssortmentGroup(a); setSku('All'); setSkuMulti(new Set()); setHiddenGraphs(new Set()); };

  // Dynamic title: Customer — Assortment — SKU (only show levels that are filtered)
  const titleParts = [
    activeName,
    assortmentGroup !== 'All' ? assortmentGroup : null,
    sku !== 'All' ? sku.replace('INFUZIONS ', '').replace('BHUJA ', '') : null,
  ].filter(Boolean);
  const chartTitle = titleParts.join(' — ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h2 style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 600 }}>Scan Sales Forecast</h2>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
          {lastActualWeek ? `Week ending ${weekEndingLabel(lastActualWeek)} · SCANv2` : 'Loading SCANv2…'}
        </div>
      </div>

      {/* Row 1 — Customer + Metric + Period */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>Customer</span>
        {['WW', 'Coles', 'Metcash', 'ALDI', 'Costco'].map(c => (
          <button key={c} className={customer === c ? 'pill-active' : 'pill-inactive'} onClick={() => {
            setCustomer(c);
            setBrand('All');
            setAssortmentGroup('All');
            setSku('All');
            setSkuMulti(new Set());
            setHiddenGraphs(new Set());
            // Non-scan customers have no RSV price data — switch to Units so forecast lines show
            if (['ALDI', 'Costco', 'Metcash'].includes(c)) setMetric('Units');
          }}>
            {c === 'WW' ? 'Woolworths' : c}
          </button>
        ))}
        <div style={{ width: 1, height: 20, background: 'var(--border-soft)', margin: '0 8px' }} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>View</span>
        {['RSV', 'Units'].map(m => (
          <button key={m} className={metric === m ? 'pill-active' : 'pill-inactive'} onClick={() => setMetric(m)}>{m}</button>
        ))}
        <div style={{ width: 1, height: 20, background: 'var(--border-soft)', margin: '0 8px' }} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>Period</span>
        <select
          value={period}
          onChange={e => setPeriod(e.target.value)}
          style={{
            background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 6,
            color: 'var(--text-primary)', fontSize: 12, padding: '4px 8px', cursor: 'pointer',
          }}
        >
          {PERIOD_OPTIONS.map(p => (
            <option key={p.label} value={p.label}>{p.label}</option>
          ))}
        </select>
      </div>

      {/* Row 2 — Brand + Assortment */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>Brand</span>
        {brandOptions.map(b => (
          <button key={b} className={brand === b ? 'pill-active' : 'pill-inactive'} onClick={() => handleBrandChange(b)}>
            {b === 'Infuzions Multipack' ? 'Infuzions MP' : b}
          </button>
        ))}
        <div style={{ width: 1, height: 20, background: 'var(--border-soft)', margin: '0 8px' }} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>Assortment</span>
        {assortmentOptions.map(a => {
          const skusInGroup = a === 'All' ? relevantSkus : relevantSkus.filter(s => itemAssortmentGroup(s) === a);
          const tip = a === 'All' ? null : buildPromoTooltip(skusInGroup, customer, upcomingWeeks);
          return (
            <button key={a} className={assortmentGroup === a ? 'pill-active' : 'pill-inactive'} onClick={() => handleAssortmentChange(a)} style={{ fontSize: 11 }} title={tip || undefined}>
              {a}
            </button>
          );
        })}
      </div>

      {/* Row 3 — Individual SKU (only if assortment selected) */}
      {assortmentGroup !== 'All' && skuOptions.length > 2 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>SKU</span>
          {skuOptions.map(s => {
            const isActive = s === 'All'
              ? (skuMulti.size === 0 && sku === 'All')
              : skuMulti.size > 0 ? skuMulti.has(s) : sku === s;
            const skuObj = s === 'All' ? null : relevantSkus.find(sk => sk.product === s);
            const skuTip = skuObj ? buildPromoTooltip([skuObj], customer, upcomingWeeks) : null;
            return (
              <button
                key={s}
                className={isActive ? 'pill-active' : 'pill-inactive'}
                style={{ fontSize: 11 }}
                title={skuTip || undefined}
                onClick={e => {
                  if (s === 'All') { setSku('All'); setSkuMulti(new Set()); return; }
                  if (e.ctrlKey || e.metaKey) {
                    setSkuMulti(prev => {
                      const next = new Set(prev);
                      if (next.has(s)) { next.delete(s); } else { next.add(s); }
                      if (next.size === 0) setSku('All');
                      return next;
                    });
                    setSku('All');
                  } else {
                    setSku(s); setSkuMulti(new Set());
                  }
                }}
              >
                {s === 'All' ? 'All' : s.replace('INFUZIONS ', '').replace('BHUJA ', '').replace('INFUZIONS', '')}
                {skuMulti.size > 0 && s !== 'All' && <span style={{ marginLeft: 3, fontSize: 9 }}>{skuMulti.has(s) ? '✓' : ''}</span>}
              </button>
            );
          })}
          {skuMulti.size > 0 && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>{skuMulti.size} selected · Ctrl+click to toggle</span>}
        </div>
      )}

      {/* Row 4 — Breakdown toggle */}
      {assortmentGroup !== 'All' && sku === 'All' && skuMulti.size === 0 && filteredSkus.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>Breakdown</span>
          <button className={breakdownMode === 'assortment' ? 'pill-active' : 'pill-inactive'} onClick={() => setBreakdownMode('assortment')}>
            Assortment total
          </button>
          <button className={breakdownMode === 'sku' ? 'pill-active' : 'pill-inactive'} onClick={() => setBreakdownMode('sku')}>
            Individual SKUs
          </button>
        </div>
      )}

      {/* Row 5 — Line visibility toggles */}
      {(() => {
        const lines = [
          { key: 'actual',    label: 'Actual',          color: activeColor },
          { key: 'ly',        label: 'LY',              color: LINE_COLORS.ly },
          { key: 'custFcst',  label: 'Cust Forecast',   color: LINE_COLORS.custFcst },
          { key: 'mfgFcst',   label: 'Mfg Forecast',    color: LINE_COLORS.mfgFcst },
          { key: 'avgPrice',  label: 'Avg Price',        color: '#a855f7' },
        ];
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>Lines</span>
            {lines.map(({ key, label, color: lc }) => {
              const on = visibility[key];
              return (
                <button
                  key={key}
                  onClick={() => setVisibility(v => ({ ...v, [key]: !v[key] }))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    background: on ? 'var(--border)' : 'transparent',
                    border: `1px solid ${on ? lc : 'var(--border-soft)'}`,
                    borderRadius: 20, padding: '3px 10px',
                    fontSize: 12, cursor: 'pointer', color: on ? 'var(--text-primary)' : 'var(--text-muted)',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ width: 10, height: 2, background: on ? lc : 'var(--border-soft)', borderRadius: 1 }} />
                  {label}
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* KPI cards — YTD actual + 13W cust forecast + 13W mfg forecast */}
      {lastActualWeek && (() => {
        const actualWeeks = activeWeeks.filter(w => w.actual != null && w.week > 0);
        const ytdActual   = actualWeeks.reduce((s, w) => s + (w.actual || 0), 0);
        const fcstWeeks13 = activeWeeks.filter(w => w.mfgFcst != null && w.week > lastActualWeek).slice(0, 13);
        const custFcst13  = activeWeeks.filter(w => w.custFcst != null && w.week > lastActualWeek).slice(0, 13);
        const mfg13w      = fcstWeeks13.reduce((s, w) => s + (w.mfgFcst  || 0), 0);
        const cust13w     = custFcst13.reduce((s,  w) => s + (w.custFcst || 0), 0);
        const fmtK = v => metric === 'Units' ? `${(v / 1000).toFixed(0)}K u` : `$${(v / 1e6).toFixed(2)}M`;
        const cards = [
          { label: `${activeName} YTD Actual`,      value: fmtK(ytdActual), color: activeColor,           sub: ['ALDI','Costco','Metcash'].includes(customer) ? 'FORECAST.xlsx sell-in actuals' : 'SCANv2 actuals' },
          { label: `${activeName} Cust Fcst 13W`,   value: cust13w ? fmtK(cust13w) : '—',  color: LINE_COLORS.custFcst,  sub: customer === 'WW' ? 'WWFORECAST baseline units' : customer === 'Coles' ? 'COLESFORECAST DC units' : 'Not available' },
          { label: `${activeName} Mfg Fcst 13W`,    value: mfg13w ? fmtK(mfg13w) : '—',    color: LINE_COLORS.mfgFcst,   sub: 'FORECAST.xlsx sell-in' },
        ];
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {cards.map(k => (
              <div key={k.label} className="kpi-card">
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{k.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: k.color }}>{k.value}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{metric === 'Units' ? 'units' : 'scan RSV'} · {k.sub}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Forecast Bias Dashboard */}
      <BiasDashboard weeks={activeWeeks} metric={metric} confirmedLastWeek={(() => {
        if (!lastActualWeek) return null;
        // A week is confirmed complete if its end date (Tuesday) is before today
        const weekEnd = new Date(FY_START);
        weekEnd.setDate(weekEnd.getDate() + (lastActualWeek - 1) * 7 + 6);
        weekEnd.setHours(23, 59, 59, 0);
        return weekEnd < new Date() ? lastActualWeek : lastActualWeek - 1;
      })()} />

      {/* Main chart(s) */}
      {breakdownMode === 'assortment' || sku !== 'All' || skuMulti.size > 0 ? (
        <ForecastChart
          title={chartTitle}
          color={activeColor}
          weeks={activeWeeks}
          metric={metric}
          retailer={activeRetailerKey}
          showPrice={showPrice}
          filteredAssortments={filteredAssortments}
          lastActualWeek={lastActualWeek}
          visibility={visibility}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {skuBreakdownItems.filter(item => !hiddenGraphs.has(item.label)).map(item => (
            <div key={item.label} style={{ position: 'relative' }}>
              <ForecastChart
                title={`${activeName} — ${item.label}`}
                color={activeColor}
                weeks={item.weeks}
                metric={metric}
                retailer={activeRetailerKey}
                showPrice={showPrice}
                filteredAssortments={item.filteredAssortments}
                lastActualWeek={lastActualWeek}
                visibility={visibility}
              />
              <button
                onClick={() => setHiddenGraphs(prev => new Set([...prev, item.label]))}
                title="Hide this chart"
                style={{
                  position: 'absolute', top: 10, right: 10,
                  background: 'var(--border)', border: '1px solid var(--border-soft)',
                  borderRadius: 4, padding: '2px 6px', fontSize: 10,
                  cursor: 'pointer', color: 'var(--text-muted)', zIndex: 1,
                }}
              >✕</button>
            </div>
          ))}
          {hiddenGraphs.size > 0 && (
            <button
              onClick={() => setHiddenGraphs(new Set())}
              style={{ alignSelf: 'flex-start', fontSize: 11, background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', color: 'var(--text-body)' }}
            >
              Show all ({hiddenGraphs.size} hidden)
            </button>
          )}
        </div>
      )}

      {/* Scan Data Table */}
      {lastActualWeek && (
        <ScanDataTable
          weeks={activeWeeks}
          metric={metric}
          retailer={activeRetailerKey}
          filteredAssortments={filteredAssortments}
          lastActualWeek={lastActualWeek}
          activeColor={activeColor}
          showSku={breakdownMode === 'sku' && assortmentGroup !== 'All' && sku === 'All' && skuMulti.size === 0}
          skuItems={skuBreakdownItems}
        />
      )}

      <div className="card" style={{ background: 'var(--bg-elevated)', padding: '12px 16px' }}>
        <div style={{ color: 'var(--text-muted)', fontSize: 12, lineHeight: 1.8 }}>
          <strong style={{ color: 'var(--text-primary)' }}>Actuals</strong> — SCANv2 scan data (Majans SKUs). Extended view shows FY2025 as historical.
          <br />
          <strong style={{ color: 'var(--text-primary)' }}>LY</strong> — Prior year same week (FY2025) from SCANv2.
          <br />
          <strong style={{ color: LINE_COLORS.custFcst }}>Cust Forecast</strong> — WW: WWFORECAST baseline units. Coles: COLESFORECAST DC units summed. RSV estimated via last known avg price.
          <br />
          <strong style={{ color: LINE_COLORS.mfgFcst }}>Mfg Forecast</strong> — Majans sell-in from FORECAST.xlsx (carton units × consumer units per carton). For WW/Coles/Metcash: overlays all weeks alongside actuals. For ALDI/Costco: past weeks show on the Actual line (sell-in), future weeks show here.
          <br />
          <strong style={{ color: 'var(--text-primary)' }}>Bias</strong> — Trailing average vs last actual week for last {BIAS_WINDOW} weeks of selected filter.
          <br />
          <strong style={{ color: 'var(--text-primary)' }}>Avg Price</strong> — Shown when single SKU selected. Calculated from SCANv2 avgPrice × units. Hover to see promo type &amp; tactics.
        </div>
      </div>
    </div>
  );
}
